import { ActionIcon, Button, Fieldset, Group, Stack, Switch, Textarea, TextInput } from "@mantine/core";
import { randomId } from "@mantine/hooks";
import { IconGripVertical, IconX } from "@tabler/icons-react";
import { ReactNode } from "@tanstack/react-router";
import { Reorder, useDragControls } from "motion/react";
import { useState } from "react";
import { useComposeFormContext } from "shared/components/message/compose/context";
import { useFormState } from "shared/hooks/useFormState";
import { arr } from "shared/utils/array";
import { maybe } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";

export const PollEditor = () => {
    const form = useComposeFormContext();
    const [poll, setPoll] = useFormState(form, "poll");
    const questionError = form.errors["poll.content"];
    const optionsError = form.errors["poll.options"];

    return (
        <Stack>
            <Switch
                label="Enabled"
                checked={!!poll}
                onChange={(e) => {
                    if (!e.target.checked) {
                        void notify
                            .confirm("Are you sure you want to delete this poll? This action cannot be undone.")
                            .then(({ close }) => {
                                setPoll(undefined);
                                form.clearFieldError("poll.options");
                                form.clearFieldError("poll.content");
                                close();
                            });

                        return;
                    }

                    setPoll({
                        content: "",
                        options: [],
                    });
                }}
            />
            <Fieldset
                variant={!!optionsError ? "default" : "unstyled"}
                style={{ borderColor: "var(--mantine-color-error)" }}
                legend={<span style={{ color: "var(--mantine-color-error)" }}>{optionsError}</span>}
                disabled={!poll}
            >
                <Stack>
                    <Textarea
                        label="Question"
                        minRows={3}
                        value={poll?.content ?? ""}
                        onChange={(e) => !!poll && setPoll({ ...poll, content: e.target.value })}
                        error={questionError}
                        placeholder="Enter question..."
                    />
                    <Reorder.Group
                        as="div"
                        axis="y"
                        dragListener={false}
                        values={poll?.options ?? []}
                        onReorder={(next) => !!poll && setPoll({ ...poll, options: next })}
                    >
                        {poll?.options.map((option, i) => (
                            <DragItem
                                key={option.id}
                                value={option}
                                onKbdMove={(dir) =>
                                    !!poll &&
                                    setPoll({
                                        ...poll,
                                        options: arr(poll.options)
                                            .move({ from: i, by: dir == "down" ? 1 : -1 })
                                            .take(),
                                    })
                                }
                            >
                                <TextInput
                                    flex={1}
                                    value={option.content}
                                    error={form.errors[`poll.options.${i}.content`]}
                                    placeholder="Response option..."
                                    onChange={(e) =>
                                        !!poll &&
                                        setPoll({
                                            ...poll,
                                            options: arr(poll.options)
                                                .update({
                                                    by: (prev) => ({ ...prev, content: e.target.value }),
                                                    where: ({ id }) => id == option.id,
                                                })
                                                .take(),
                                        })
                                    }
                                />
                                <ActionIcon
                                    variant="transparent"
                                    onClick={() => !!poll && setPoll({ ...poll, options: poll.options.toSpliced(i, 1) })}
                                    aria-label="Delete option"
                                >
                                    <IconX size={16} />
                                </ActionIcon>
                            </DragItem>
                        ))}
                    </Reorder.Group>
                    <Group>
                        <Button
                            variant="subtle"
                            onClick={() =>
                                !!poll &&
                                setPoll({ ...poll, options: poll.options.concat([{ id: randomId(), content: "", isSelected: false }]) })
                            }
                            disabled={maybe(poll?.options)?.take((it) => it.length > 9)}
                        >
                            Add Response
                        </Button>
                    </Group>
                </Stack>
            </Fieldset>
        </Stack>
    );
};

/** @private */
const DragItem = (props: { key: string; value: unknown; children: ReactNode; onKbdMove: (dir: "up" | "down") => void }) => {
    const ctrls = useDragControls();
    const [isDragging, setIsDragging] = useState(false);

    return (
        <Reorder.Item
            as="div"
            value={props.value}
            key={props.key}
            dragListener={false}
            dragControls={ctrls}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={() => setIsDragging(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <Group mt="xs">
                <ActionIcon
                    variant="transparent"
                    style={{ cursor: isDragging ? "grabbing" : "grab" }}
                    onPointerDown={(e) => ctrls.start(e)}
                    onKeyDown={(e) => {
                        // for acessibility purposes, we need a way to rearrange items via the keyboard
                        if (["ArrowUp", "ArrowDown"].includes(e.key)) e.preventDefault(); // prevent screen scrolling by arrow keys
                        if (e.key == "ArrowUp") props.onKbdMove("up");
                        if (e.key == "ArrowDown") props.onKbdMove("down");
                    }}
                    aria-label="Reorder option (use arrow keys or drag)"
                >
                    <IconGripVertical size={16} />
                </ActionIcon>
                {props.children}
            </Group>
        </Reorder.Item>
    );
};
